import { useState, useCallback } from 'react';
import { SubscriptionRequiredError } from '../services/api';

export type SubscriptionLevel = 'founder' | 'pro';

interface UseSubscriptionGuardProps {
  companyId: string;
  feature?: string;
  title?: string;
  description?: string;
  requiresLevel?: SubscriptionLevel; // 'founder' or 'pro' - defaults to 'pro'
}

export function useSubscriptionGuard({
  companyId,
  feature = 'AI Features',
  title,
  description,
  requiresLevel = 'pro',
}: UseSubscriptionGuardProps) {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalProps, setUpgradeModalProps] = useState({
    feature,
    title,
    description,
  });

  const withSubscriptionGuard = useCallback(
    async <T>(asyncFunction: () => Promise<T>, customFeature?: string): Promise<T | null> => {
      try {
        const result = await asyncFunction();
        return result;
      } catch (error) {
        if (error instanceof SubscriptionRequiredError) {
          // Update modal props if custom feature is provided
          setUpgradeModalProps({
            feature: customFeature || feature,
            title: title || `Unlock ${customFeature || feature}`,
            description:
              description ||
              (requiresLevel === 'founder'
                ? `Subscribe to Founder to access ${customFeature || feature}`
                : `Upgrade to Pro Activation to access ${customFeature || feature}`),
          });
          setShowUpgradeModal(true);
          return null;
        }
        // Re-throw other errors
        throw error;
      }
    },
    [companyId, feature, title, description, requiresLevel]
  );

  const closeUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false);
  }, []);

  return {
    withSubscriptionGuard,
    showUpgradeModal,
    closeUpgradeModal,
    upgradeModalProps: {
      ...upgradeModalProps,
      companyId,
      isOpen: showUpgradeModal,
      onClose: closeUpgradeModal,
    },
  };
}

// Convenience function to check if a feature requires Founder
export function requiresFounder(): SubscriptionLevel {
  return 'founder';
}

// Convenience function to check if a feature requires Pro
export function requiresPro(): SubscriptionLevel {
  return 'pro';
}
